import type { ProductType } from "@component/assets/AbstractLeadForm/types";
import type {
	AbstractListingCalculatorProps,
	AbstractListingCalculatorState,
} from "@component/assets/AbstractListingCalculator/AbstractListingCalculator";
import { AbstractListingCalculator } from "@component/assets/AbstractListingCalculator/AbstractListingCalculator";
import type { ResultListResponse } from "@component/assets/AbstractListingCalculator/types";
import { Config } from "@config";
import { LandingCover } from "@landing/components/LandingCover";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import autobind from "autobind-decorator";
import { __ } from "bm-ui-kit";
import dayjs from "dayjs";
import lzString from "lz-string";
import type { ReactNode } from "react";
import React from "react";
import { MortgageResultList } from "./MortgageResultList";
import { MortgageSearchForm } from "./MortgageSearchForm";
import { BASIC_SEARCH_FIELDS } from "./data";
import {
	calculate,
	exportXls,
	getDefaultParams,
	getGuestPeriodCategory,
	isCSOKVariant,
	responseToResult,
	searchParamsToRequest,
} from "./domain";
import type {
	MortgageListingCalculatorVariant,
	MortgageRequest,
	MortgageResponseItem,
	MortgageSearchParams,
	MortgageSort,
	PeriodCategory,
} from "./types";
import "./style.scss";

interface Props extends AbstractListingCalculatorProps {
	title?: ReactNode;
	description?: ReactNode;
	button?: string;

	badge?: ReactNode;
	variant?: MortgageListingCalculatorVariant;
}

interface State
	extends AbstractListingCalculatorState<MortgageSearchParams, MortgageRequest, MortgageResponseItem, MortgageSort> {
	sortToCalculate: MortgageSort;
}

export class MortgageCalculator extends AbstractListingCalculator<
	MortgageSearchParams,
	MortgageRequest,
	MortgageResponseItem,
	MortgageSort,
	Props,
	State
> {
	// EventParams
	protected readonly productTypes: string = "MORTGAGE";
	protected readonly componentType: EventComponentType = "listing-calculator";

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			sortToCalculate: undefined,
		} as unknown as State;
	}

	protected getDefaultValues(): MortgageSearchParams {
		const { variant, highlightedBank } = this.props;
		const isCsok = isCSOKVariant(variant);
		const hasExtra = Config.getCurrentUser().hasRole("MORTGAGE");
		const params = getDefaultParams(variant);
		const newParams = {
			...params,
			eligibleForCsokPlus: true,
			csok: isCsok ? true : params.csok,
			calculationDate: dayjs().format("YYYY-MM-DD"),
			onePerBank: !highlightedBank,
		};

		if (highlightedBank) {
			newParams.interestPeriod = "TEN_YEARS";

			if (highlightedBank === "otp") {
				newParams.interestPeriod = "FULL";
			}
			(newParams as any).interestPeriod1 = newParams.interestPeriod; // TODO: Ezt javítani kell
			newParams.interestPeriods = undefined;
		} else if (!hasExtra) {
			newParams.interestPeriod = undefined;
			newParams.interestPeriods = getGuestPeriodCategory(params.interestPeriods as PeriodCategory[]);
		}

		return newParams;
	}

	public renderContent(): ReactNode {
		const { variant, highlightedBank } = this.props;
		const { searchParams, sort } = this.state;
		const {
			title = __("Lakáshitel kalkulátor"),
			description = __("Spórolj százezreket! Személyre szabott megoldást adunk hiteligényedre."),
			button = __("Lakáshitelek összehasonlítása"),
			fakeHeader,
			badge,
		} = this.props;

		return (
			<>
				<LandingCover
					title={title}
					badge={badge}
					subTitle={description}
					fakeHeader={fakeHeader}
					showSubTitleOnMobileDevice={variant === undefined}
					variant={variant === "GREEN_LOAN" ? "green" : undefined}
				>
					<MortgageSearchForm
						buttonText={button}
						variant={variant}
						multipleInterestPeriods={!highlightedBank}
						sort={sort}
						{...this.getSearchFormProps()}
					/>
				</LandingCover>
				{searchParams && (
					<MortgageResultList
						key={`list_${lzString.compressToEncodedURIComponent(JSON.stringify(searchParams))}`}
						variant={variant}
						{...this.getResultListProps()}
					/>
				)}
			</>
		);
	}

	@autobind
	protected onSortChange(sort: MortgageSort): void {
		const { searchParams } = this.state;

		this.setState(
			{
				searchParams: { ...searchParams, sort },
				sort,
				sortToCalculate: sort,
			},
			() => super.onSortChange(sort),
		);
	}

	protected getParamsFromUrl(name: string, value: string): any {
		if (name === "interestPeriods") {
			const list = [value].flat(2).join(",").split(",");

			if (Config.getCurrentUser().hasRole("MORTGAGE")) {
				return list;
			}

			return getGuestPeriodCategory(list as PeriodCategory[]).filter((item) =>
				list.find((interestPeriod) => item.includes(interestPeriod)),
			);
		}

		return super.getParamsFromUrl(name, value);
	}

	protected getRequestFromSearchParams(searchParams: MortgageSearchParams): MortgageRequest {
		return searchParamsToRequest(searchParams, this.state?.sort);
	}

	protected async getResult(request: MortgageRequest, _searchParams: MortgageSearchParams): Promise<any> {
		const { sortToCalculate } = this.state;
		this.setState({
			request: { ...request, sort: sortToCalculate },
			sort: sortToCalculate,
		});

		return calculate({ ...request, sort: sortToCalculate });
	}

	protected getResultList(
		request: MortgageRequest,
		_searchParams: MortgageSearchParams,
		response: any,
	): ResultListResponse<MortgageResponseItem> {
		const { marketingFilter = this.marketingFilter } = this.props;

		return responseToResult(request, response, marketingFilter);
	}

	@autobind
	protected async onExport(searchParams: MortgageSearchParams): Promise<string> {
		return exportXls(searchParams);
	}

	protected getClassName(): string {
		const { variant } = this.props;

		return `${super.getClassName()} mortgage-calculator ${
			variant === undefined ? "variant-base" : `variant-${variant}`
		}`;
	}

	protected getProductType(): ProductType {
		return "MORTGAGE";
	}

	protected getBasicSearchFields(): string[] {
		const { variant, highlightedBank } = this.props;

		return [
			...BASIC_SEARCH_FIELDS,
			variant === "CSOK" || ["TAMOGATOTT_FELUJITAS", "GREEN_LOAN"].includes(variant)
				? "numberOfChildren"
				: undefined,
			variant !== "TAMOGATOTT_FELUJITAS" && variant !== "CSOK" ? "onlyGreen" : undefined,
			variant !== "CSOK" && !highlightedBank ? "onePerBank" : undefined,
		].filter((fieldName) => fieldName !== undefined);
	}

	protected isDetailed(searchParams: MortgageSearchParams): boolean {
		return super.isDetailed(searchParams, ["bankCodes"]);
	}

	protected getComponentName(): string {
		return "MortgageCalculator";
	}
}
